/**
 * roleView — the real client-aware landing for the web PWA.
 *
 * The web app is CLIENT-only: clients sign in with passkeys, staff (all three
 * roles) use the admin console via Auth0 SSO. Self-signup always creates a
 * `client` user, so a passkey session is always a client — there is no longer
 * an agent surface here (it was a Phase-0 leftover, removed 2026-06-22). The
 * view is derived from the passkey session (features/auth/session.ts) plus a
 * one-shot dossier lookup:
 *
 *   - no session            → `client-new`   (anonymous discovery landing)
 *   - client, no dossier    → `client-new`   (welcome — start a transaction)
 *   - client, ≥1 dossier    → `client-returning` (home — transaction in progress)
 *
 * Auth *state* (`authenticated`) is kept SEPARATE from the *view* so the Shell
 * can show a real avatar (not a "Sign in" CTA) to a signed-in client who hasn't
 * started a transaction yet — the welcome surface is the discovery page, not a
 * login wall.
 *
 * The provider is mounted as the Shell layout element (router.tsx). Because the
 * auth screens (/sign-up, /login) live OUTSIDE the layout, navigating back to a
 * Shell route after login remounts this provider and it re-reads the freshly
 * saved session — no manual storage subscription needed.
 *
 * The dossier list route is self-scoping: a client-role caller only ever sees
 * their own dossiers (document-svc list_dossiers), so `limit: 1` ordered by
 * created_at desc both answers new-vs-returning and yields the latest dossier id
 * for the "My documents" nav.
 */

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { dossierService } from "@re-platform/api-client";

import { api } from "../lib/api";
import { loadSession, sessionExpired } from "../features/auth/session";

export type RoleView = "client-new" | "client-onboarding" | "client-returning";

export interface RoleViewState {
  /** A valid passkey session is present. */
  authenticated: boolean;
  /** The client dossier lookup is still resolving. */
  loading: boolean;
  view: RoleView;
  /** Latest dossier id for a returning client — drives the My-documents nav. */
  dossierId: string | null;
}

const RoleViewContext = createContext<RoleViewState | null>(null);

export function RoleViewProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const session = loadSession();

  // Redirect to /login (with a flag) when a stored session has expired so the
  // user sees a "session expired" prompt rather than the marketing landing page.
  useEffect(() => {
    if (session == null && sessionExpired()) {
      void navigate("/login?expired=1", { replace: true });
    }
  }, [session, navigate]);

  // ⚠️ The SAME key and the SAME params as every other `my-dossiers` reader
  // (ClientHome's application list, MyDossier's switcher). A react-query key is
  // the cache identity: this used to ask for `limit: 1` under the shared key, and
  // because this provider is the Shell layout it mounts FIRST — so its one-item
  // array became what the home page and the switcher rendered, and a client with
  // five applications saw exactly one. Nothing looked wrong in either component;
  // the request they issued was never the one that filled their cache.
  //
  // One list serves all three (this provider only ever reads the newest, below),
  // which also makes it one request instead of two.
  const dossiersQuery = useQuery({
    queryKey: ["my-dossiers"],
    queryFn: () => dossierService.list(api, { limit: 50 }),
    enabled: session != null,
    staleTime: 30_000,
  });

  let state: RoleViewState;
  if (session == null) {
    state = {
      authenticated: false,
      loading: false,
      view: "client-new",
      dossierId: null,
    };
  } else {
    // Client: new vs returning decided by the dossier lookup. A query error
    // (expired token, network) falls back to the welcome surface, which is the
    // safe, non-blocking landing.
    const latest = dossiersQuery.data?.[0];
    // Authenticated clients with no dossier go through the onboarding wizard;
    // unauthenticated visitors see the marketing landing (client-new).
    const view: RoleView = dossiersQuery.isPending
      ? "client-onboarding" // hold onboarding until we know
      : latest
        ? "client-returning"
        : "client-onboarding";
    state = {
      authenticated: true,
      loading: dossiersQuery.isPending,
      view,
      dossierId: latest?.id ?? null,
    };
  }

  return (
    <RoleViewContext.Provider value={state}>
      {children}
    </RoleViewContext.Provider>
  );
}

export function useRoleView(): RoleViewState {
  const ctx = useContext(RoleViewContext);
  if (!ctx) {
    throw new Error("useRoleView must be used within RoleViewProvider");
  }
  return ctx;
}
